import * as React from "react";
import {none, option, Option} from "ts-option";
import DictionaryItem from "../../../model/dictionary/DictionaryItem";
import PageHeaderView from "../../body/PageHeaderView";
import PageContentView from "../../body/PageContentView";
import Loading from "../../ui/loading/Loading";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import "./activityTemplateDetails.scss";
import UploadTaskItemsModal from "./modal/UploadTaskItemsModal";
import {isDefined, notifyBugsnagAndShowToast} from "../../../utils/Utils";
import Toast from "../../helper/Toast";
import DictionarySortableItemsTable from "../common/DictionarySortableItemsTable";
import DropDown from "../../../hiberUiKit/src/molecules/DropDown/DropDown";
import TaskUtils from "../../../utils/TaskUtils";
import Task from "../../../model/task/Task";

interface IProps {
	/**
	 * Selected activity template
	 */
	activityTemplate: Option<DictionaryItem>;
	/**
	 * Activity templates
	 */
	activityTemplates: Option<DictionaryItem[]>;
	/**
	 * Job activity templates
	 */
	jobActivityTemplates: Option<DictionaryItem[]>;
	/**
	 * Task
	 */
	tasks: DictionaryItem[]
	/**
	 * Download task items
	 * @param {string} taskId
	 * @returns {Promise<any>}
	 */
	downloadTaskItems: (taskId: string) => Promise<any>
	/**
	 * Set activity template tasks
	 * @param {string[]} tasksIds
	 * @returns {Promise<any>}
	 */
	setActivityTemplateTasks: (tasksIds: string[]) => Promise<any>
	/**
	 * Fetch activity templates
	 */
	fetchActivityTemplates: () => void
	/**
	 * Fetch activity template tasks
	 * @returns {Promise<Task[]>}
	 */
	fetchActivityTemplateTasks: () => Promise<Task[]>

}

interface IState {
	originalTasks: Option<DictionaryItem[]>;
	updatedTasks: DictionaryItem[];
	uploadingTaskId: Option<string>;
	exportLoading: { [key: string]: boolean };
	dirty: boolean;
	templatesGroupedByTasks: Option<{ [key: string]: DictionaryItem[] }>
	isLoading: boolean
}

type Props = IProps

export default class EditActivityTemplate extends React.Component<Props, IState> {

	constructor(props: Props) {
		super(props);

		this.state = {
			originalTasks: none,
			updatedTasks: [],
			uploadingTaskId: none,
			exportLoading: {},
			dirty: false,
			templatesGroupedByTasks: none,
			isLoading: false

		};
		this.renderTaskLabel = this.renderTaskLabel.bind(this);
		this.saveChanges = this.saveChanges.bind(this);
		this.renderTableElement = this.renderTableElement.bind(this);
	}

	componentDidMount(): void {
		this.props.fetchActivityTemplateTasks().then(tasks => {
			const dictionaryItems = tasks.map(t => new DictionaryItem(t.id, t.title));
			this.setState({originalTasks: option(dictionaryItems), updatedTasks: dictionaryItems});
		}).catch((err) => notifyBugsnagAndShowToast(err, "Fetch activity template tasks"));
		this.fetchActivityTask();
	}

	componentWillReceiveProps() {
		this.fetchActivityTask();
	}

	fetchActivityTask() {
		if (this.props.activityTemplates.isDefined && this.props.jobActivityTemplates.isDefined &&
			this.state.templatesGroupedByTasks.isEmpty && !this.state.isLoading) {
			const activityTemplates = this.props.activityTemplates.get;
			const jobActivityTemplates = this.props.jobActivityTemplates.get;
			this.setState({isLoading: true}, () => TaskUtils.getGroupedTasks(activityTemplates, jobActivityTemplates,
				(templatesGroupedByTasks) => this.setState({templatesGroupedByTasks, isLoading: false})))

		}
	}

	render() {
		return (
			<div>
				<PageHeaderView header={this.props.activityTemplate.map(at => at.name).getOrElse("")}>
					<ButtonPrimary label={"Save changes"} onClick={this.saveChanges} disabled={!this.state.dirty}/>
				</PageHeaderView>
				<PageContentView>
					<DropDown items={this.getNonUsedTasks()}
							  itemToLabel={this.renderTaskLabel}
							  itemToId={(task: DictionaryItem) => task.id}
							  placeholder={"Selected task will be added to survey"}
							  onSelect={(task: DictionaryItem) => this.addTaskToActivityTemplate(task)}/>
					<Loading isLoading={this.state.originalTasks.isEmpty}>
						<DictionarySortableItemsTable items={this.state.updatedTasks}
													  onRowClick={() => {}}
													  onOrderChange={(items) => this.setState(
														  {dirty: true, updatedTasks: items})
													  }
													  renderHeaderBody={this.renderTableHead}
													  renderRowBody={this.renderTableElement}/>
					</Loading>
					{this.state.uploadingTaskId.map(tId => {
						return (
							<UploadTaskItemsModal taskId={tId}
												  onSuccess={() => this.setState({uploadingTaskId: none})}
												  onCancel={() => this.setState({uploadingTaskId: none})}/>
						)
					}).orNull}
				</PageContentView>
			</div>
		)
	}

	renderTaskLabel(task: DictionaryItem) {
		const templatesWithTask = this.state.templatesGroupedByTasks.flatMap(tgbt => option(tgbt[task.id]));
		return <span style={templatesWithTask.isDefined ? {fontStyle: "italic"} : {}}>
			{task.name}
			{templatesWithTask.map(templates => <span>{` (${templates.map(t => t.name).join(", ")})`}</span>).orNull}
		</span>;
	}

	renderTableHead() {
		return (
			<React.Fragment>
				<th>Task name</th>
				<th style={{width: "500px"}}>Actions</th>
			</React.Fragment>
		);
	}

	renderTableElement(item: DictionaryItem) {
		return (
			<React.Fragment>
				<td>{item.name}</td>
				<td className={"c-activity-template-details_buttons"}>
					<ButtonPrimary label={"Import"}
								   onClick={() => this.setState({uploadingTaskId: option(item.id)})}/>
					<ButtonPrimary label={"Export"}
								   disabled={isDefined(this.state.exportLoading[item.id])}
								   onClick={() => this.downloadTaskItems(item)}/>
					<ButtonPrimary label={"Remove"}
								   disabled={false}
								   onClick={() => this.removeTaskFromActivityTemplate(item)}/>
				</td>
			</React.Fragment>
		)
	}

	downloadTaskItems(task: DictionaryItem) {
		const loadingState = this.state.exportLoading;
		loadingState[task.id] = true;
		this.setState({exportLoading: loadingState}, () => this.props.downloadTaskItems(task.id)
			.then(() => this.stopExportLoading(task))
			.catch((err) => {
				this.stopExportLoading(task);
				notifyBugsnagAndShowToast(err, "Download task items");
			}));
	}

	stopExportLoading(task: DictionaryItem) {
		const loadingState = this.state.exportLoading;
		delete loadingState[task.id];
		this.setState({exportLoading: loadingState});
	}

	addTaskToActivityTemplate(task: DictionaryItem) {
		this.setState({dirty: true, updatedTasks: this.state.updatedTasks.concat(task)});
	}

	removeTaskFromActivityTemplate(task: DictionaryItem) {
		this.setState({dirty: true, updatedTasks: this.state.updatedTasks.filter(t => t.id != task.id)})
	}

	getNonUsedTasks() {
		const map: { [key: string]: string } = {};
		this.state.updatedTasks.forEach(task => map[task.id] = task.name);
		return this.props.tasks.filter(task => !isDefined(map[task.id]));
	}

	saveChanges() {
		this.props.setActivityTemplateTasks(this.state.updatedTasks.map(t => t.id))
			.then(() => {
				this.setState({dirty: false}, () => {
					Toast.success("Activity template tasks updated", "Update activity template");
				});
			})
			.catch((error) => {
				let msg = "Activity template tasks update failed";
				if (error && error.response && error.response.status == 400) {
					msg = "Cannot find one or more tasks"
				}
				else if (error && error.response && error.response.status == 404) {
					msg = "Activity template not found"
				}
				notifyBugsnagAndShowToast(error, "Update activity template", msg)
			})
	}
}


